import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Home, Menu, Loader } from 'lucide-react';
import { motion } from 'framer-motion';
import { addDoc, collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-hot-toast';
import { useMenu } from '../contexts/MenuContext';

const OrderSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useMenu();
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const orderCreated = useRef(false); // Prevent duplicate order creation

  useEffect(() => {
    const handleOrderCreation = async () => {
      // Prevent duplicate execution (React StrictMode runs useEffect twice)
      if (orderCreated.current) {
        console.log('⚠️ Order already processed, skipping...');
        setLoading(false);
        return;
      }

      try {
        // Check if we have session_id from Stripe redirect
        const sessionId = searchParams.get('session_id');
        
        if (sessionId) {
          console.log('🔵 Processing Stripe payment success...');
          
          // Mark as processing immediately to prevent duplicate runs
          orderCreated.current = true;
          
          // Check if order with this sessionId already exists
          const existingOrderQuery = query(
            collection(db, 'orders'),
            where('sessionId', '==', sessionId),
            limit(1)
          );
          const existingOrders = await getDocs(existingOrderQuery);
          
          if (!existingOrders.empty) {
            // Order already exists, just show it
            const existingOrder = existingOrders.docs[0].data();
            console.log('✅ Order already exists:', existingOrder.orderNumber);
            setOrderNumber(existingOrder.orderNumber);
            setTotal(existingOrder.total);
            setOrderItems(existingOrder.items || []);
            
            // Clear pending order data and cart
            sessionStorage.removeItem('pendingOrder');
            clearCart();
            
            setLoading(false);
            return;
          }
          
          // Verify payment with backend
          const response = await fetch(`/api/checkout-success?session_id=${sessionId}`);
          const result = await response.json();

          if (!result.success) {
            throw new Error('Payment verification failed');
          }

          console.log('✅ Payment verified:', result);

          // Get cart items from sessionStorage
          const cartData = sessionStorage.getItem('pendingOrder');
          if (!cartData) {
            console.error('❌ No pending order found in sessionStorage');
            throw new Error('No pending order found. Your payment was successful but we couldn\'t find your order details. Please contact support with your payment confirmation.');
          }

          const pendingOrder = JSON.parse(cartData);
          
          // Get next order number
          const ordersRef = collection(db, 'orders');
          const q = query(ordersRef, orderBy('orderNumber', 'desc'), limit(1));
          const querySnapshot = await getDocs(q);
          
          let generatedOrderNumber = 1;
          if (!querySnapshot.empty) {
            const lastOrder = querySnapshot.docs[0].data();
            const lastOrderNumber = lastOrder.orderNumber || 0;
            generatedOrderNumber = lastOrderNumber > 10000 ? 1 : lastOrderNumber + 1;
          }

          // Create order in Firestore
          const orderData = {
            ...pendingOrder,
            orderNumber: generatedOrderNumber,
            sessionId: sessionId,
            paymentIntentId: result.paymentIntentId,
            paymentStatus: 'completed',
            paidAt: new Date(),
            status: 'pending',
            createdAt: new Date(),
          };

          await addDoc(collection(db, 'orders'), orderData);
          
          console.log('✅ Order created:', generatedOrderNumber);
          
          setOrderNumber(generatedOrderNumber);
          setTotal(pendingOrder.total);
          setOrderItems(pendingOrder.items || []);
          
          // Clear pending order from sessionStorage AFTER setting state
          sessionStorage.removeItem('pendingOrder');
          
          // Clear cart using context function
          clearCart();
          
          toast.success('Order placed successfully!');
        } else if (location.state) {
          // Fallback to location state if no session_id
          orderCreated.current = true;
      setOrderNumber(location.state.orderNumber);
      setTotal(location.state.total);
        } else {
          orderCreated.current = true;
          setError('No order information found');
        }
      } catch (error: any) {
        console.error('❌ Error processing order:', error);
        setError(error.message || 'Failed to process order');
        // Don't show toast error if we successfully created the order
        if (!orderNumber) {
          toast.error('There was an issue processing your order. Please check the Order Panel or contact support.');
        }
      } finally {
        setLoading(false);
      }
    };

    handleOrderCreation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader className="h-12 w-12 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Processing your order...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center"
        >
          <div className="text-red-500 mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Order Processing Error
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Home className="h-4 w-4 inline mr-2" />
            Go Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
        </motion.div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Order Confirmed!
        </h1>
        
        <p className="text-gray-600 mb-6">
          Thank you for your order. We'll start preparing it right away.
        </p>

        {orderNumber && (
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <p className="text-sm text-gray-600 mb-1">Order Number</p>
            <p className="text-2xl font-bold text-red-600 mb-4">#{orderNumber}</p>
            
            {orderItems.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3 text-left">Order Details</h4>
                <div className="space-y-2 mb-4">
                  {orderItems.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between items-start text-sm text-left">
                      <div className="flex-1">
                        <p className="font-medium">{item.quantity}x {item.menuItem.name}</p>
                        {item.selectedExtras && item.selectedExtras.length > 0 && (
                          <p className="text-xs text-gray-500 ml-4">
                            {item.selectedExtras.map((e: any) => `+${e.quantity}x ${e.extra.name}`).join(', ')}
                          </p>
                        )}
                      </div>
                      <span className="font-medium ml-3">
                        £{(item.menuItem.price * item.quantity + 
                           (item.selectedExtras?.reduce((sum: number, e: any) => sum + (e.extra.price * e.quantity), 0) || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-3 flex justify-between font-bold">
                    <span>Total:</span>
                    <span>£{total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/menu')}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Menu className="h-4 w-4 mr-2" />
              View Menu
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Home className="h-4 w-4 mr-2" />
              Home
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderSuccess;
